import { z } from 'zod';

const shelfSchema = z.object({
  title: z.string(),
  id: z.string(),
  web_id: z.number().optional(),
  tags: z.string().optional(),
  stories: z.array(
    z.object({
      id: z.string(),
      type: z.string().optional(),
      name: z.string().optional(),
      cover: z.string().nullish(),
      author: z.string().optional(),
      original_story_id: z.number().optional(),
      url_slug: z.string().nullish(),
      publisher: z.string().nullable().optional(),
      teaser: z.string().nullish(),
      summary: z
        .object({
          content: z.string(),
        })
        .nullish(),
      test_cover: z
        .object({
          title: z.string().nullish(),
        })
        .nullish(),
    }),
  ),
});

export type Shelf = z.infer<typeof shelfSchema>;

const paginationSchema = z.object({
  current_cursor: z.string(),
  next_cursor: z.string(),
  page_items: z.number(),
  is_last_page: z.boolean(),
});

export type PaginationSchema = z.infer<typeof paginationSchema>;

/** `category_path` is added by galatea-web once the shelf's category page has been verified;
 *  `null` means the category page has no linkable path. */
const discoveryShelfSchema = shelfSchema.extend({
  category_path: z.string().nullable().optional(),
});

export type DiscoveryShelf = z.infer<typeof discoveryShelfSchema>;

export const discoveryContentResolve = z.object({
  shelves: z.array(discoveryShelfSchema),
  pagination: paginationSchema,
});

export type DiscoveryContentResolve = z.infer<typeof discoveryContentResolve>;

const shelfRedirectSchema = z.object({
  slug: z.string(),
  permanent: z.boolean(),
});

export type ShelfRedirect = z.infer<typeof shelfRedirectSchema>;

export const shelfPageContentResolve = z.object({
  // A web shelf served by slug may answer with content (`shelf`) or a redirect directive
  // (`redirect`, for a deprecated shelf). Legacy web_id shelves always return `shelf`.
  shelf: shelfSchema.optional(),
  redirect: shelfRedirectSchema.optional(),
  // The language codenames serving this slug, the requested one included (api-go#1670). Optional
  // on purpose: api-go sends it `omitempty` and omits it entirely for legacy-dispatch shelves,
  // which have no locale siblings, so requiring it would fail the parse on every web_id shelf.
  published_languages: z.array(z.string()).optional(),
});

/**
 * Minimal projection for the shelf next-page existence probe. The v3 shelves response carries
 * no total_items or is_last_page (api-go PaginationIndexedV3), so whether another page exists
 * must be probed with an items_per_page=1 request addressing the next page's first story.
 */
export const shelfStoriesProbeResolve = z.object({
  pagination: z.object({
    current_page_items: z.number(),
  }),
});

export type ShelfPageContentResolve = z.infer<typeof shelfPageContentResolve>;

export const shelfPageMetadataResolve = z.object({
  webId: z.string(),
  language: z.string(),
  metaTitle: z.string(),
  metaDescription: z.string(),
  keywordPlaceholder: z.string(),
  categoryDescription: z.string(),
  hasFallbacks: z.boolean(),
});

const shelfRecommendationSchema = z.object({
  id: z.string(),
  name: z.string(),
  original_story_id: z.number(),
});

export type ShelfRecommendationSchema = z.infer<typeof shelfRecommendationSchema>;

export const youMayAlsoLikeResolve = z.array(
  z.object({
    shelf: shelfSchema,
  }),
);

export type YouMayAlsoLikeResolve = z.infer<typeof youMayAlsoLikeResolve>;

export const shelvesResponseSchema = z.object({
  tag_slug: z.string(),
  tag_name: z.string(),
  shelf_name: z.string(),
  shelf_slug: z.string(),
  shelf_web_id: z.number(),
  shelf_covers: z.array(z.string()),
});

export type ShelvesResponseSchema = z.infer<typeof shelvesResponseSchema>;

export const webShelfResponseSchema = z.object({
  slug: z.string(),
  name: z.string(),
  updated_at: z.string(),
});

export type WebShelfResponseSchema = z.infer<typeof webShelfResponseSchema>;

export const siteMapResponseSchema = z.object({
  tag_kinds: z.array(
    z.object({
      tag_kind_slug: z.string(),
      shelves: z.array(shelvesResponseSchema),
    }),
  ),
  // Tolerant on purpose: a missing, null or malformed value must cost only the web shelves. The
  // whole response shares one safeParse, so a strict field here would blank /browse, the shelf
  // sitemap and the navbar as well. Per item, so one bad record costs itself and not the list;
  // goApi drops the nulls and reports them, since a silent drop hides a contract change.
  web_shelves: z.array(webShelfResponseSchema.nullable().catch(null)).nullish().catch([]),
});

export type SiteMapResponseSchema = z.infer<typeof siteMapResponseSchema>;
